import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Loading, Typography } from '@p81-common/ui';
import azureAuthService from 'services/azureAuthService';
import { env } from 'env';
import {
  startConversation,
  ConversationSession
} from './helpers/copilotService';
import ChatWidget from './parts/ChatWidget/ChatWidget';
import createClasses from './styles';

const CopilotChat = () => {
  const { t } = useTranslation('copilotChat');
  const classes = createClasses();
  const [session, setSession] = useState<ConversationSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const account = azureAuthService.getActiveAccount();
  const userId = account?.localAccountId || account?.homeAccountId || 'user';

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const envBaseUrl = env.REACT_APP_COPILOT_ENVIRONMENT_BASE_URL;
        const botSchema = env.REACT_APP_COPILOT_BOT_SCHEMA;

        if (!envBaseUrl || !botSchema) {
          throw new Error(t('ERROR_NOT_CONFIGURED'));
        }

        const result = await startConversation(envBaseUrl, botSchema, userId);
        if (cancelled) return;
        setSession(result);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t('ERROR_UNKNOWN'));
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const handleTokenExchange = useCallback(
    async (resourceUri: string) => {
      const scope = resourceUri.endsWith('/access_as_user')
        ? resourceUri
        : `${resourceUri}/access_as_user`;

      try {
        const response = await azureAuthService.acquireTokenSilent({
          scopes: [scope],
          account: account!
        });
        return response.accessToken;
      } catch {
        const response = await azureAuthService.acquireTokenPopup({
          scopes: [scope]
        });
        return response.accessToken;
      }
    },
    [account]
  );

  return (
    <Box className={classes.container}>
      {error && (
        <Box className={classes.centered} sx={{ p: 3 }}>
          <Typography color="error">
            {t('ERROR_CONNECT_PREFIX')}
            {error}
          </Typography>
        </Box>
      )}
      {!error && !session && (
        <Box className={classes.centered}>
          <Loading />
        </Box>
      )}
      {!error && session && (
        <ChatWidget
          token={session.token}
          conversationId={session.conversationId}
          streamUrl={session.streamUrl}
          directLineUrl={session.directLineUrl}
          userId={userId}
          onTokenExchange={handleTokenExchange}
        />
      )}
    </Box>
  );
};

export default CopilotChat;
